// 마을마다 다른 이야기·주민·대사(정본: docs/TOWN-SCENARIO.md). 시설 역할·자리·기능은 그대로 두고 사람·말만 마을마다 다르다.
// 큰 줄기 「꺼진 봉화」: 마을마다 그 지역 보스가 끼친 피해가 있고, 지역보스(n-10)를 잡으면 같은 사람이 다른 말을 한다.
// three·DOM import 금지(공유 데이터). 판정·저장에 쓰지 않는다(표시 전용).
import type { NpcRole } from './rpg';

export interface Resident {
  readonly name: string;
  readonly title: string;
  /** 그 마을 지역보스(n-10)를 깨기 전 인사 */
  readonly before: string;
  /** 깬 뒤 인사 */
  readonly after: string;
  /** 3D 옷 색 곱(전용 모델이 없어 같은 모델의 다른 사람으로 보이게). 없으면 흰색 */
  readonly tint?: number;
  /** UI 초상 CSS 필터(3D 색 곱과 같은 결). 전용 그림(art)이 없을 때만 쓴다 */
  readonly artFilter?: string;
  /** 전용 UI 그림 id(public/assets/ui/npc/<art>.webp 초상 · <art>_cut.webp 상반신, TOWN-SCENARIO T4). 없으면 역할 기본 그림 + artFilter */
  readonly art?: string;
}

export interface TownStory {
  /** 도착 카드 한 줄: 그 마을의 사정 */
  readonly tagline: string;
  readonly residents: Readonly<Partial<Record<NpcRole, Resident>>>;
}

/** 마을 번호(1..6) → 이야기(⑤⑥은 3개월 C2: TOWN-SCENARIO 2절 가안 그대로. ⑥은 앞선 마을의 아는 얼굴) */
export const TOWN_STORIES: Readonly<Record<number, TownStory>> = {
  1: {
    tagline: '봉화가 아직 타는 마을',
    residents: {
      // 마을①은 기존 사람·기존 인사 그대로(rpg.VILLAGE_NPCS), 보스 뒤 인사만 더한다
      shop: { name: '미라', title: '잡화상', before: '어서 와요. 던전에 들어가기 전에 몸을 지킬 갑옷부터 챙겨요.',
        after: '숲길이 열리니 상단 마차가 다시 들어와요. 갑옷이랑 물약, 넉넉히 골라 가요.' },
      forge: { name: '브론', title: '대장장이', before: '칼이 무뎌 보이는군. 골드만 있으면 새로 벼린 대검을 내주지.',
        after: '고목이 잠들었으니 다음은 잿불 협곡이군. 전초기지 길드장 하그에게 내 이름을 대게.' },
      trainer: { name: '세르', title: '검술 교관',
        before: '돌진으로 무리 속에 뛰어들고, 폭풍으로 끌어모은 뒤, 낙화로 한 번에 내려찍어라. 서로 다른 기술을 끊김 없이 이어 쓰면 연계가 오른다. 주력으로 삼을 기술도 골라라.',
        after: '숲을 뚫었으니 연계는 몸에 붙었겠지. 다음 땅은 불이다. 주력 기술을 다시 골라 둬라.' },
    },
  },
  2: {
    tagline: '첫 불을 도둑맞은 대장장이 성채',
    residents: {
      shop: { name: '루카', art: 'res_t2_luka', title: '보급관', tint: 0xd8b08a, artFilter: 'sepia(.35) hue-rotate(-12deg) saturate(1.15)',
        before: '배급표 없이는 빵 한 조각도 못 내드려요. …당신은 예외로 하죠. 성문 밖에 나갈 사람한테 아낄 건 없으니까요.',
        after: '첫 불이 돌아와서 배급이 풀렸어요! 장부 정리할 맛이 나네요. 장비든 물약이든 마음껏 고르세요.' },
      forge: { name: '하그', art: 'res_t2_hag', title: '길드장', tint: 0xe09070, artFilter: 'hue-rotate(-18deg) saturate(1.35) brightness(.94)',
        before: '대주술사 놈이 내 화로에서 첫 불을 훔쳐 갔다. 남은 불씨로도 강화는 해 주지. 대신 그놈 골렘을 부숴 와.',
        after: '첫 불이 돌아왔다. 들리나? 이제야 쇠가 노래를 하는군. 뭐든 벼려 주마.' },
      trainer: { name: '이그나', art: 'res_t2_igna', title: '수비대장', tint: 0xff9a80, artFilter: 'hue-rotate(-35deg) saturate(1.45)',
        before: '성문은 해 지면 닫힌다. 협곡에 들어가려면 그 전에 돌아와. 서리 항구와는 연락이 끊긴 지 오래다.',
        after: '협곡의 불길이 잦아들었다. 계엄은 풀렸어. 서리 항구에 가거든 늙은 볼크를 찾아라. 내 이름을 대면 안다.' },
    },
  },
  3: {
    tagline: '바다가 얼고 등대가 꺼진 항구',
    residents: {
      shop: { name: '마르타', art: 'res_t3_marta', title: '선구상', tint: 0xbccbe8, artFilter: 'hue-rotate(195deg) saturate(.8) brightness(1.04)',
        before: '남편 배가 저 얼음에 갇혔어요. …괜찮아요, 기다리는 건 익숙하니까. 추위 막을 장비부터 챙겨 가요.',
        after: '바다가 녹았어요. 남편 배가 오늘 아침 돌아왔어요! 정말 고마워요. 필요한 건 뭐든 말해요.' },
      forge: { name: '에릭', art: 'res_t3_erik', title: '닻 대장장이', tint: 0xa8c4e0, artFilter: 'hue-rotate(175deg) saturate(.9) brightness(1.06)',
        before: '요즘은 닻 대신 얼음 깨는 정만 두드려. 묘지 쪽 종소리가 들리면 망치질도 멈추게 되더라. 무기는 맡겨.',
        after: '배가 나가니까 다시 닻을 두드려! 이 망치 소리, 오랜만이지? 강화도 신나게 해 줄게.' },
      trainer: { name: '볼크', art: 'res_t3_volk', title: '늙은 항해사', tint: 0xd4dbe6, artFilter: 'grayscale(.55) brightness(1.08)',
        before: '등대가 꺼진 바다는 무덤이야. 리치를 잡기 전엔 아무도 못 나가. 늪지 장터엔 독안개가 퍼졌다더군.',
        after: '등대가 다시 켜졌군. 바다 냄새가 돌아왔어. 늪지 장터에 가면 사냥꾼 야나를 찾아. 늪은 그 녀석이 제일 잘 알지.' },
      jeweler: { name: '하엘', art: 'res_t3_hael', title: '보석상',
        before: '서리 항구에 온 걸 환영해요. 리치의 냉기에 보석이 자꾸 금이 가요. 박고 빼고 합치는 건 제게 맡겨요.',
        after: '냉기가 걷히니 보석 빛이 살아나요. 보석은 색이 효과, 등급이 크기예요. 박고 빼고 합치는 건 제게 맡겨요.' },
    },
  },
  4: {
    tagline: '독안개에 손님이 끊긴 늪 위 시장',
    residents: {
      shop: { name: '피피', art: 'res_t4_pipi', title: '흥정꾼', tint: 0xf2c070, artFilter: 'hue-rotate(18deg) saturate(1.5) brightness(1.04)',
        before: '어이쿠, 손님! 안개 때문에 사흘째 개미 한 마리 안 왔어요. 오늘 첫 손님이니 좋은 물건부터 보여 드릴게요!',
        after: '안개가 걷히니 손님이 줄을 서요! 외상값부터 받으러 다녀야겠네. 당신은 언제든 먼저예요.' },
      forge: { name: '고르', art: 'res_t4_gor', title: '뗏목 대장장이', tint: 0x9cb486, artFilter: 'hue-rotate(58deg) saturate(.9) brightness(.96)',
        before: '습기 때문에 쇠가 하룻밤이면 녹슬어. 그래도 내 망치는 안 녹슬지. 뭘 벼려 줄까?',
        after: '안개가 걷히니 쇠가 바싹 마르는군. 이 맛에 망치를 들지. 맡겨.' },
      trainer: { name: '야나', art: 'res_t4_yana', title: '늪 사냥꾼', tint: 0xa6cc92, artFilter: 'hue-rotate(78deg) saturate(1.15)',
        before: '히드라는 머리를 잘라도 다시 난다. 한꺼번에 몰아서 끝내. 독안개 속에선 오래 버티지 마.',
        after: '히드라가 가라앉았다. 내 눈으로 봤어. 남쪽 사막엔 움직이는 도시가 있다던데, 길은 아직 모래에 묻혀 있다.' },
      alchemist: { name: '네라', art: 'res_t4_nera', title: '연금술사',
        before: '독안개 탓에 약초가 독초로 변했어요. 그래도 독이자 약이니 비약은 달여 드릴게요. 물약도 챙겨 가요.',
        after: '약초가 다시 약 냄새를 내요. 이제야 제대로 된 비약을 달일 수 있겠어요. 물약도 챙겨 가요.' },
    },
  },
  5: {
    tagline: '말라 가는 오아시스, 움직이는 도시',
    residents: {
      shop: { name: '사디', title: '물 상인', tint: 0xf0cf88, artFilter: 'sepia(.5) hue-rotate(-8deg) saturate(1.3) brightness(1.04)',
        before: '물은 금보다 비싸요. 샘이 해마다 말라 가거든요. …그래도 손님한테 팔 장비와 물약은 넉넉해요.',
        after: '샘이 다시 솟아요! 오늘만큼은 물값을 깎아 드릴게요. 장비도 물약도 마음껏 골라요.' },
      forge: { name: '누르', title: '천막 대장장이', tint: 0xe0a86c, artFilter: 'sepia(.45) hue-rotate(-22deg) saturate(1.5)',
        before: '모래 군주가 물길을 막은 뒤로 담금질 물이 모자란다. 그래도 칼은 벼려 주지. 무기는 맡겨.',
        after: '물이 돌아오니 쇠가 제 소리를 내는군. 축제 전에 칼부터 새로 벼려 주마. 맡겨.' },
      trainer: { name: '카림', title: '호위대장', tint: 0xdcb890, artFilter: 'sepia(.35) hue-rotate(10deg) saturate(1.2)',
        before: '대상단은 다음 우물까지 가지 못하고 멈췄다. 폐도의 모래 군주만 쓰러뜨리면 길이 열린다. 기술부터 점검해 둬라.',
        after: '모래 군주가 쓰러졌다. 대상단이 다시 움직인다. 다음 길은 구름 위 요새로 이어진다더군. 몸 성히 다녀와라.' },
    },
  },
  6: {
    tagline: '다섯 마을 사람이 모인 마지막 거점',
    // 이 마을만은 앞선 마을의 아는 얼굴이 보상이다(TOWN-SCENARIO 2절): 하그(②)·마르타(③)·야나(④)가 요새 일을 맡는다
    residents: {
      shop: { name: '마르타', art: 'res_t3_marta', title: '요새 보급관', tint: 0xbccbe8, artFilter: 'hue-rotate(195deg) saturate(.8) brightness(1.04)',
        before: '남편 배가 여기까지 짐을 실어 와요. 구름 위까지 올라온 사람들 몫으로 장비를 쌓아 뒀어요. 필요한 건 말해요.',
        after: '성채가 조용해졌어요. 이제 정말 끝이 보여요. 남은 짐도 전부 풀 테니 뭐든 골라요.' },
      forge: { name: '하그', art: 'res_t2_hag', title: '요새 대장장이', tint: 0xe09070, artFilter: 'hue-rotate(-18deg) saturate(1.35) brightness(.94)',
        before: '첫 불을 되찾은 놈이 여기까지 올라왔군. 이 요새 화로는 구름 위에서도 안 꺼진다. 뭐든 벼려 주마.',
        after: '성채의 불이 꺼졌다. 이제 여섯 봉화가 다 타오르겠지. 마지막까지 벼려 주마.' },
      trainer: { name: '야나', art: 'res_t4_yana', title: '요새 정찰대장', tint: 0xa6cc92, artFilter: 'hue-rotate(78deg) saturate(1.15)',
        before: '저기 보랏빛 성채가 보이지? 정찰대가 본 건 허공 위의 문뿐이야. 들어가기 전에 주력 기술부터 다시 골라 둬.',
        after: '성채가 가라앉았다. 내 눈으로 봤어. 이제 하늘이 맑아질 거야. 수고했어.' },
    },
  },
};

/** 그 마을 그 역할의 주민(없으면 undefined: 기본 NPC 정의를 쓴다) */
export function townResident(town: number, role: NpcRole): Resident | undefined {
  return TOWN_STORIES[town]?.residents[role];
}

/** 마을 n의 지역보스(n-10)를 깼는지: 노말 순차 진행 수 ≥ n × 10(지역 = 스테이지 10판) */
export function townBossCleared(town: number, cleared: number): boolean {
  return town >= 1 && cleared >= town * 10;
}

/** 도착 카드 한 줄(없으면 '') */
export function townTagline(town: number): string {
  return TOWN_STORIES[town]?.tagline ?? '';
}
